
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { careerCategories } from '@/data/careerCategories';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';

const CareerSelection = () => {
  const navigate = useNavigate();
  const [selectedCareers, setSelectedCareers] = useState<Array<number | null>>([null, null, null]);
  const [isValid, setIsValid] = useState(false);

  // Sort career categories by name
  const sortedCareers = [...careerCategories].sort((a, b) => a.name.localeCompare(b.name));

  // Validate if at least one career is selected
  useEffect(() => {
    const hasSelection = selectedCareers.some(career => career !== null);
    setIsValid(hasSelection);
  }, [selectedCareers]);

  const handleCareerChange = (index: number, value: string) => {
    const newCareerSelections = [...selectedCareers];
    newCareerSelections[index] = Number(value);
    setSelectedCareers(newCareerSelections);
  };

  const handleNext = () => {
    if (!isValid) {
      toast.error("Please select at least one career option");
      return;
    }

    // Filter out null values and store selected careers in localStorage
    const validSelections = selectedCareers.filter((id): id is number => id !== null);
    localStorage.setItem('preferred_careers', JSON.stringify(validSelections));
    
    // Navigate to the assessment page
    navigate('/assessment');
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <main className="flex-grow flex items-center justify-center p-4 sm:p-6 md:p-8">
        <Card className="w-full max-w-3xl shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-brand-blue">Select Your Preferred Careers</CardTitle>
            <CardDescription>
              Choose up to three career paths you're interested in exploring
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {[0, 1, 2].map((index) => (
              <div key={index} className="space-y-2">
                <label className="text-sm font-medium">
                  Career Option {index + 1}
                </label>
                <Select 
                  value={selectedCareers[index]?.toString() || ""} 
                  onValueChange={(value) => handleCareerChange(index, value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a career" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {sortedCareers.map((career) => (
                      <SelectItem key={career.id} value={career.id.toString()}>
                        {career.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}

            <div className="flex justify-between pt-6">
              <Button 
                variant="outline" 
                onClick={() => navigate('/')}
              >
                Back to Home
              </Button>
              <Button 
                onClick={handleNext} 
                disabled={!isValid}
              >
                Continue to Assessment
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
      
      <Footer />
    </div>
  );
};

export default CareerSelection;
